/**
 * ScheduleMeetingDialog Component
 * Form dialog for creating new customer meetings
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCreateMeeting } from "@/hooks/useCustomerMeetings";
import { isFutureDateTime, toISODateString } from "@/lib/dateUtils";
import { getCachedCustomers } from "@/lib/offline/db";
import type { EventType, MeetingType } from "@/types/meeting.types";

interface ScheduleMeetingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: number | null;
  projectId?: number | null;
  initialDate?: Date | null;
}

export function ScheduleMeetingDialog({
  isOpen,
  onClose,
  customerId,
  projectId,
  initialDate,
}: ScheduleMeetingDialogProps) {
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(customerId);
  const [meetingDate, setMeetingDate] = useState(
    initialDate ? toISODateString(initialDate) : toISODateString(new Date())
  );
  const [meetingTime, setMeetingTime] = useState("09:00");
  const [eventType, setEventType] = useState<EventType>("meeting");
  const [durationType, setDurationType] = useState<MeetingType>("consultation");
  const [duration, setDuration] = useState("60");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");

  // Reset duration to 0 when switching to task or reminder
  const handleEventTypeChange = (newEventType: EventType) => {
    setEventType(newEventType);
    if (newEventType !== "meeting") {
      setDuration("0");
    } else if (duration === "0") {
      setDuration("60"); // Reset to default for meetings
    }
  };

  const createMeeting = useCreateMeeting();

  // Update meeting date when initialDate prop changes
  useEffect(() => {
    if (initialDate) {
      setMeetingDate(toISODateString(initialDate));
    }
  }, [initialDate]);

  // Fetch customers list if no customerId provided
  // WITH OFFLINE SUPPORT: Falls back to IndexedDB when network unavailable
  const { data: customers = [], isLoading: customersLoading } = useQuery({
    queryKey: ["customers-list"],
    queryFn: async () => {
      try {
        // Try network first
        const response = await fetch(
          `${import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8787"}/api/v1/customers?limit=1000`
        );
        if (!response.ok) throw new Error("Failed to fetch customers");
        const result = await response.json();
        return result.data as Array<{
          id: number;
          first_name: string;
          last_name: string;
          email: string;
        }>;
      } catch (error) {
        console.log("[Schedule Dialog] Network failed, checking offline cache...");

        // Network failed - check IndexedDB
        const cachedCustomers = await getCachedCustomers();

        if (cachedCustomers.length === 0) {
          throw error; // Re-throw if no offline data
        }

        // Transform CachedCustomer format to match expected format
        // CachedCustomer has 'name' field, UI expects 'first_name' and 'last_name'
        const transformedCustomers = cachedCustomers.map((customer) => {
          const nameParts = customer.name.split(" ");
          const firstName = nameParts[0] || "Unknown";
          const lastName = nameParts.slice(1).join(" ") || "";

          return {
            id: customer.id,
            first_name: firstName,
            last_name: lastName,
            email: customer.email || "",
          };
        });

        console.log(
          `[Schedule Dialog] Serving ${transformedCustomers.length} customers from offline cache`
        );
        return transformedCustomers;
      }
    },
    enabled: !customerId && isOpen,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate customer selection
    const finalCustomerId = customerId || selectedCustomerId;
    if (!finalCustomerId) {
      alert("Please select a customer.");
      return;
    }

    // For meetings, validate future date/time and duration
    if (eventType === "meeting") {
      if (!isFutureDateTime(meetingDate, meetingTime)) {
        alert("Please select a future date and time for the meeting.");
        return;
      }

      const durationMinutes = Number.parseInt(duration);
      if (durationMinutes < 15 || durationMinutes > 480) {
        alert("Duration must be between 15 and 480 minutes.");
        return;
      }
    }

    // Parse duration (0 if not set for tasks/reminders)
    const durationMinutes = duration ? Number.parseInt(duration) : 0;

    try {
      await createMeeting.mutateAsync({
        customer_id: finalCustomerId,
        project_id: projectId,
        meeting_date: meetingDate || toISODateString(new Date()),
        meeting_time: meetingTime || "00:00",
        event_type: eventType,
        meeting_type: eventType === "meeting" ? durationType : "consultation",
        duration_minutes: durationMinutes,
        status: "scheduled",
        location: location.trim() || null,
        notes: notes.trim() || null,
      });

      // Reset form
      setMeetingDate(toISODateString(new Date()));
      setMeetingTime("09:00");
      setEventType("meeting");
      setDurationType("consultation");
      setDuration("60");
      setLocation("");
      setNotes("");

      onClose();
    } catch (error) {
      // Error is handled by the mutation hook with toast
      console.error("Failed to create meeting:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] overflow-visible">
        <DialogHeader>
          <DialogTitle>Schedule Event</DialogTitle>
          <DialogDescription>
            Schedule a new calendar event. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer Selector (only if customerId not provided) */}
          {!customerId && (
            <div className="space-y-2">
              <Label htmlFor="customer">
                Customer <span className="text-destructive">*</span>
              </Label>
              <Select
                value={selectedCustomerId?.toString() || ""}
                onValueChange={(v) => setSelectedCustomerId(Number(v))}
              >
                <SelectTrigger id="customer">
                  <SelectValue placeholder={customersLoading ? "Loading customers..." : "Select a customer..."} />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4} className="z-[9999] max-h-60">
                  {customersLoading ? (
                    <SelectItem value="loading" disabled>Loading customers...</SelectItem>
                  ) : customers.length === 0 ? (
                    <SelectItem value="empty" disabled>No customers found</SelectItem>
                  ) : (
                    customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id.toString()}>
                        {customer.first_name} {customer.last_name} ({customer.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Event Type */}
          <div className="space-y-2">
            <Label htmlFor="event-type">
              Event Type <span className="text-destructive">*</span>
            </Label>
            <Select value={eventType} onValueChange={handleEventTypeChange}>
              <SelectTrigger id="event-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="task">Task</SelectItem>
                <SelectItem value="reminder">Reminder</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Meeting Type (only show if event_type = 'meeting') */}
          {eventType === "meeting" && (
            <div className="space-y-2">
              <Label htmlFor="meeting-type">
                Meeting Type <span className="text-destructive">*</span>
              </Label>
              <Select value={durationType} onValueChange={(v) => setDurationType(v as MeetingType)}>
                <SelectTrigger id="meeting-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consultation">Consultation</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                  <SelectItem value="project-review">Project Review</SelectItem>
                  <SelectItem value="quote-discussion">Quote Discussion</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="meeting-date">
                Date {eventType === "meeting" && <span className="text-destructive">*</span>}
                {eventType !== "meeting" && (
                  <span className="text-muted-foreground text-xs">(optional)</span>
                )}
              </Label>
              <Input
                id="meeting-date"
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                required={eventType === "meeting"}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meeting-time">
                Time {eventType === "meeting" && <span className="text-destructive">*</span>}
                {eventType !== "meeting" && (
                  <span className="text-muted-foreground text-xs">(optional)</span>
                )}
              </Label>
              <Input
                id="meeting-time"
                type="time"
                value={meetingTime}
                onChange={(e) => setMeetingTime(e.target.value)}
                required={eventType === "meeting"}
              />
            </div>
          </div>

          {/* Duration (only for meetings) */}
          {eventType === "meeting" && (
            <div className="space-y-2">
              <Label htmlFor="duration">
                Duration (minutes) <span className="text-destructive">*</span>
              </Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger id="duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                  <SelectItem value="180">3 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              placeholder="e.g., Customer site, Office, Video call"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Meeting agenda, topics to discuss..."
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMeeting.isPending}>
              {createMeeting.isPending ? "Scheduling..." : "Schedule Event"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
